import { Box } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useReducer } from "react";
import { useNavigate, useParams } from "react-router";
import { useApi } from "@platform/api";

import { submitEvent } from "../../utils/tracking";
import AlertError from "../../components/alertError";
import {
  ACCEPTED_FILE_MINE_TYPES,
  RFP_FILES_TYPES,
  ROUTE_NAMES,
} from "../../constants";
import { useAnalyticsData } from "../../contexts/analyticsContext";
import { useToastNotificationService } from "../../hooks";
import {
  downloadBinaryFile,
  extractErrorMessage,
  validateFileMIMEType,
  validateFileName,
  validateFileSize,
} from "../../utils";

import AnalystRFPDetails from "./analystRfpDetails";
import { formatDate, parseUploadedFiles } from "./helpers";
import RfpDetails from "./rfpDetails";

function reducer(state, action) {
  return {
    ...state,
    ...action,
  };
}

const initState = {
  loading: false,
  data: null,
  assignedAnalyst: null,
  rfpCreationDate: null,
  rfpDueDate: null,
  rfpEffectiveDate: null,
  carrierNames: null,
  uploadedFiles: [],
  documentsFiles: [],
  proposalFiles: [],
  pageError: null,
  fileUpdateError: null,
  proposalUpdateError: null,
  isDocumentLoading: false,
  isProposalLoading: false,
  uploadMarketingPresentationError: null,
  broker: null,
};

function Broker({ isAdmin = false }) {
  const axiosInstance = useApi();
  const navigate = useNavigate();
  const { id } = useParams();
  const [state, dispatch] = useReducer(reducer, initState);
  const { dispatch: analyticsDispatch } = useAnalyticsData();
  const { createToastNotification } = useToastNotificationService();

  const { error, refetch, isLoading } = useQuery({
    enabled: Boolean(id),
    refetchOnWindowFocus: false,
    queryKey: ["prepareRfpDetails", id],
    queryFn: async () => {
      const { data } = await axiosInstance.post(
        `brokerportal/v1/prepareRfpDetails`,
        {
          rfpId: Number(id),
        },
      );

      const _dispatchState = {
        data,
        assignedAnalyst: data.rfpAnalyst,
        rfpCreationDate: formatDate(data.rfp.creationDate),
        rfpDueDate: data.rfp.details?.dueDate,
        rfpEffectiveDate: data.rfp.details?.effectiveDate,
        carrierNames: data.rfp.details.carriers?.join(", "),
        documentsFiles: parseUploadedFiles(data.rfpFiles),
        proposalFiles: parseUploadedFiles(data.rfpProposals),
        marketingPresentation:
          data.rfp.rfpMarketingPresentation?.marketingPresentation,
        marketingCreationDate: data.rfp.rfpMarketingPresentation?.updatedAt,
        marketingSizeBytes: data.rfp.rfpMarketingPresentation?.contentLength,
        broker: data.broker,
        additionalDocuments: data.additionalDocuments,
      };
      dispatch(_dispatchState);
      analyticsDispatch({
        companyName:
          data.rfp?.client?.name || data.rfp?.details?.clientInfo?.name,
        brokerName: data.broker.name,
        rfpId: data.rfp.id,
      });

      return _dispatchState;
    },
  });

  useEffect(() => {
    dispatch({ loading: isLoading });
  }, [isLoading]);

  useEffect(() => {
    if (error) {
      dispatch({ loading: false, pageError: "Something went wrong!" });
    }
  }, [error]);

  useEffect(() => {
    if (!id) {
      navigate(ROUTE_NAMES.HOME);
    }
  }, [id, navigate]);

  async function handleFileDownload(file, rfpFileType) {
    const isRfpDocument = rfpFileType === RFP_FILES_TYPES.DOCUMENT;
    try {
      submitEvent("Download Proposals");
      dispatch({
        [isRfpDocument ? "isDocumentLoading" : "isProposalLoading"]: true,
      });
      const { data } = await axiosInstance.post(
        `brokerportal/v1/getRfpFile`,
        {
          downloadLink: file.downloadLink,
        },
        {
          responseType: "blob",
        },
      );

      downloadBinaryFile(data, file.name);
    } catch (err) {
      dispatch({ fileUpdateError: extractErrorMessage(err) });
    } finally {
      dispatch({
        [isRfpDocument ? "isDocumentLoading" : "isProposalLoading"]: false,
      });
    }
  }

  async function handleFileDelete(file) {
    try {
      dispatch({ isProposalLoading: true, proposalUpdateError: null });
      await axiosInstance.post(`brokerportal/v1/deleteRfpProposal`, {
        rfpId: state.data.rfp.id,
        fileName: file.displayName,
      });
      dispatch({
        proposalFiles: state.proposalFiles.filter((f) => f.name !== file.name),
      });
    } catch (err) {
      dispatch({
        isProposalLoading: false,
        proposalUpdateError: extractErrorMessage(err),
      });
    } finally {
      dispatch({ isProposalLoading: false });
    }
  }

  async function handleDocumentUpload(files) {
    var formData = new FormData();
    formData.append("rfp", id);
    try {
      dispatch({ isProposalLoading: true, proposalUpdateError: null });
      for (const file of files) {
        formData.append(file.name, file);
      }

      const { data } = await axiosInstance.post(
        "brokerportal/v1/uploadCarrierProposal",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      dispatch({ proposalFiles: parseUploadedFiles(data) });
    } catch (err) {
      dispatch({
        proposalUpdateError: extractErrorMessage(err),
        proposalFiles: state.proposalFiles,
      });
    } finally {
      dispatch({ isProposalLoading: false });
    }
  }

  const handleAnalystChange = useCallback(
    async ({ target }) => {
      const _analyst = JSON.parse(target.value);
      try {
        dispatch({ loading: true, headerError: null });
        await axiosInstance.post(`brokerportal/v1/assignRfpAnalystAdmin`, {
          analystId: _analyst.id,
          rfpId: parseInt(id),
        });
        createToastNotification({
          severity: "success",
          message: "Analyst Assignment Successful",
        });
        await refetch();
        dispatch({ loading: false, assignedAnalyst: _analyst });
      } catch (err) {
        dispatch({
          loading: false,
          headerError: extractErrorMessage(err, "Error assigning analyst!"),
        });
      }
    },
    [axiosInstance, id, createToastNotification, refetch],
  );

  async function handleMarketingPresentationDownload() {
    try {
      submitEvent("Download Marketing Presentation");
      dispatch({ headerError: null });
      const { data } = await axiosInstance.post(
        `brokerportal/v1/downloadMarketingPresentation`,
        {
          rfpId: state.data.rfp.id,
        },
        {
          responseType: "blob",
        },
      );
      return data;
    } catch (err) {
      dispatch({
        headerError: extractErrorMessage(
          err,
          "Error downloading marketing presentation!",
        ),
      });
      throw err;
    }
  }

  async function handleDeleteMarketingPresentation() {
    try {
      dispatch({ isMarketingPresentationLoading: true, headerError: null });
      await axiosInstance.post(`brokerportal/v1/deleteMarketingPresentation`, {
        rfpId: state.data.rfp.id,
        fileName: state.marketingPresentation,
      });
      dispatch({
        isMarketingPresentationLoading: false,
        marketingPresentation: null,
      });
    } catch {
      dispatch({
        isMarketingPresentationLoading: false,
        headerError: "Error deleting marketing presentation!",
      });
    }
  }

  async function handleMarketingPresentationUpload(files) {
    const file = files[0];
    if (!file) return;
    try {
      dispatch({
        isMarketingPresentationLoading: true,
        uploadMarketingPresentationError: null,
      });
      validateFileName(file.name);
      validateFileSize(file.size, 50);
      validateFileMIMEType(file.type, ACCEPTED_FILE_MINE_TYPES);

      const formData = new FormData();
      formData.append("marketingPresentation", file);
      formData.append("rfpId", state.data.rfp.id);
      await axiosInstance.post(
        "brokerportal/v1/uploadMarketingPresentation",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      dispatch({
        marketingPresentation: file.name,
        isMarketingPresentationLoading: false,
      });
    } catch (err) {
      const errorMessage = err.message || null;
      dispatch({
        isMarketingPresentationLoading: false,
        uploadMarketingPresentationError: {
          fileName: file.name,
          error: extractErrorMessage(
            err,
            errorMessage || "Unexpected error occurred. Try again later!",
          ),
        },
      });
    }
  }

  return state.pageError ? (
    <Box mt={15}>
      <AlertError>{state.pageError}</AlertError>
    </Box>
  ) : isAdmin ? (
    <AnalystRFPDetails
      rfpId={id}
      state={state}
      dispatch={dispatch}
      refetchRpfDetails={() => refetch()}
    />
  ) : (
    <RfpDetails
      isBroker={!isAdmin}
      state={state}
      refetchRpfDetails={() => refetch()}
      dispatch={dispatch}
      onFileDownload={handleFileDownload}
      onMarketingPresentationUpload={handleMarketingPresentationUpload}
      onMarketingPresentationDownload={handleMarketingPresentationDownload}
      handleDeleteMarketingPresentation={handleDeleteMarketingPresentation}
      onDocumentUpload={handleDocumentUpload}
      onFileDelete={handleFileDelete}
      onAnalystChange={handleAnalystChange}
    />
  );
}

export default Broker;
